/*
 * @format
 */

// Starting a Cognito sign-in, shared by the app and the web app, with a
// password or a passkey.

import { fetchAuthSession, signIn, signOut } from 'aws-amplify/auth';

/**
 * Starts a Cognito sign-in. Amplify keeps one session (one per browser on the
 * web), so it refuses while it still holds one: one the app doesn't show as
 * signed in (an earlier sign-in that didn't finish, say), or on the web one
 * another tab started. With `overSession`, that session is ended and the
 * sign-in started again as asked, so the password or passkey given is still
 * the one checked. Without it, the refusal is passed on.
 */
export async function startSignIn(input, { overSession = true } = {}) {
  try {
    return await signIn(input);
  } catch (ex) {
    // Do not log: a sign-in exception can echo the submitted credentials.
    if (!overSession || ex?.name !== 'UserAlreadyAuthenticatedException') {
      throw ex;
    }
    await signOut();
    return signIn(input);
  }
}

/**
 * Amplify turns these Cognito refusals into next steps rather than throwing.
 * Throws them as the errors the sign-in screens explain; any other step is left
 * to the caller.
 */
export function throwForRefusedStep(nextStep) {
  if (nextStep?.signInStep === 'RESET_PASSWORD') {
    throw new Error('PasswordResetRequiredException');
  }
  if (nextStep?.signInStep === 'CONFIRM_SIGN_UP') {
    throw new Error('UserNotConfirmedException');
  }
}

/**
 * The tokens Amplify holds: null when it holds none, undefined when that
 * can't be told. fetchAuthSession throws when refreshing expired tokens fails
 * for want of a network, and Amplify keeps the tokens then, so a failure isn't
 * proof there are none. Each caller decides what not knowing means for it.
 */
export async function heldTokens() {
  try {
    return (await fetchAuthSession()).tokens ?? null;
  } catch {
    return undefined;
  }
}
